import { Injectable } from '@angular/core';
import { GroupAnalysisService } from './group-analysis.service';
import { GroupAnalysisStore } from './group-analysis.store';
import {
  catchError,
  EMPTY,
  finalize,
  forkJoin,
  interval,
  map,
  Observable,
  of,
  Subject,
  switchMap,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';
import {
  mapCreateTemplatePayload,
  mapCustomerSite,
  mapDailyEnergy,
  mapEnergySummary,
  mapMdbPower,
  mapTemplateApiToModel,
  mapUpdateTemplatePayload,
} from './group-analysis.mapper';
import { AnalysisTemplate, DateRangeValue } from './group-analysis.interface';
import { SettingService } from '@feature/setting';
import { DashboardService } from '@feature/dashboard';
import { DialogService } from '@core';

@Injectable({
  providedIn: 'root',
})
export class GroupAnalysisFacade {
  readonly templates = this.store.templates;
  readonly selectedTemplate = this.store.selectedTemplate;
  readonly templateDetail = this.store.templateDetail;
  readonly dailyEnergyPeriod = this.store.dailyEnergyPeriod;
  readonly dailyEnergyPieChart = this.store.dailyEnergyPieChart;
  readonly monthlyEnergyPieChart = this.store.monthlyEnergyPieChart;
  readonly dailyCategories = this.store.dailyCategories;
  readonly dailySeries = this.store.dailySeries;

  readonly startDate = this.store.startDate;
  readonly endDate = this.store.endDate;
  readonly unit = this.store.unit;

  readonly totaldailyEnergyPieChart = this.store.totaldailyEnergyPieChart;
  readonly totalmonthlyEnergyPieChart = this.store.totalmonthlyEnergyPieChart;
  readonly mdbPowerCategories = this.store.mdbPowerCategories;

  readonly mdbPowerSeries = this.store.mdbPowerSeries;
  readonly loadingTemplate = this.store.loadingTemplates;
  readonly loadingDashboard = this.store.loadingDashboard;
  readonly loading = this.store.loading;
  readonly error = this.store.error;
  readonly totalYesterdayEnergyPieChart =
    this.store.totalYesterdayEnergyPieChart;
  readonly customerSite = this.store.customerSite;
  readonly totalLastMonthlyEnergyPieChart =
    this.store.totalLastMonthlyEnergyPieChart;

  private readonly destroy$ = new Subject<void>();
  constructor(
    private readonly groupService: GroupAnalysisService,
    private readonly dashboardService: DashboardService,
    private readonly mdb: GroupAnalysisService,
    private readonly store: GroupAnalysisStore,
    public settingService: SettingService,
    private readonly dialogService: DialogService,
  ) {}
  initialize(): void {
    this.loadInitialData();

    interval(15 * 60 * 1000)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.refreshData();
      });
  }

  private loadInitialData(): void {
    this.dialogService
      .withLoading(
        forkJoin({
          dashboard: this.loadDashboard(),
          dailyEnergy: this.loadDailyEnergy('month'),
          mdbPower: this.loadMdbPower(),
        }),
      )
      .subscribe();
  }

  private refreshData(): void {
    forkJoin({
      dashboard: this.loadDashboard(),
      dailyEnergy: this.loadDailyEnergy('month'),
      mdbPower: this.loadMdbPower(),
    }).subscribe({
      error: (error) => {
        console.error('Dashboard refresh error:', error);
      },
    });
  }

  changeDailyEnergyPeriod(period: 'week' | 'month'): void {
    if (this.store.dailyEnergyPeriod() === period) {
      return;
    }

    this.loadDailyEnergy(period).subscribe();
  }

  private loadMdbPower(): Observable<void> {
    return this.dashboardService.getDashboardMDB().pipe(
      tap((response) => {
        this.store.setMdbPower(mapMdbPower(response.meta.response_data.graph));
      }),
      map(() => undefined),
      catchError((error) => {
        this.store.setError(
          this.getErrorMsg(error, 'ไม่สามารถโหลดข้อมูล MDB Power ได้'),
        );

        return of(undefined);
      }),
    );
  }
  // reload(): void {
  //   const selectedTemplate = this.store.selectedTemplate();

  //   if (!selectedTemplate) {
  //     this.initialize();
  //     return;
  //   }

  //   this.loadDashboard(selectedTemplate.id).subscribe();
  // }

  destroy(): void {
    this.store.reset();
    this.destroy$.next();
    this.destroy$.complete();
  }

  // selectTemplate(template: AnalysisTemplate): void {
  //   if (this.store.selectedTemplate()?.id === template.id) return;

  //   this.store.setSelectedTemplate(template);
  //   this.loadDashboard(template.id).subscribe();
  // }

  // changeDateRange(range: DateRangeValue): void {
  //   this.store.setDateRange(range);

  //   const selectedTemplate = this.store.selectedTemplate();
  //   if (!selectedTemplate) return;

  //   this.loadDashboard(selectedTemplate.id).subscribe();
  // }

  // private loadTemplates(selectId?: number): Observable<void> {
  //   this.store.setLoadingTemplate(true);
  //   this.store.setError(null);

  //   return this.groupService.getTemplateList().pipe(
  //     map((res) => (res.meta.response_data ?? []).map(mapTemplateApiToModel)),
  //     tap((templates) => {
  //       this.store.setTemplates(templates);
  //     }),
  //     switchMap((templates) => {
  //       const currentSelected = this.store.selectedTemplate();

  //       const selectedTemplate =
  //         templates.find((t) => t.id === selectId) ??
  //         templates.find((t) => t.id === currentSelected?.id) ??
  //         templates[0] ??
  //         null;

  //       this.store.setSelectedTemplate(selectedTemplate);

  //       if (!selectedTemplate) {
  //         this.store.clearDashboard();
  //         return of(undefined);
  //       }

  //       return this.loadDashboard(selectedTemplate.id);
  //     }),
  //     map(() => undefined),
  //     catchError((err) => {
  //       this.store.setError(this.getErrorMsg(err, 'ไม่สามารถโหลดรายการได้'));
  //      return of(undefined);
  //     }),

  //     finalize(() => {
  //       this.store.setLoadingTemplate(false);
  //     }),
  //   );
  // }

  loadDailyEnergy(period: 'week' | 'month'): Observable<void> {
    this.store.setDailyEnergyPeriod(period);

    return this.groupService
      .getDailyEnergy({
        period,
      })
      .pipe(
        tap((daily) => {
          this.store.setDailyEnergy(
            daily.meta.response_data
              ? mapDailyEnergy(daily.meta.response_data)
              : {
                  categories: [],
                  series: [],
                },
          );
        }),
        map(() => undefined),
        catchError((error) => {
          this.store.setError(
            this.getErrorMsg(error, 'ไม่สามารถโหลดข้อมูล Daily Energy ได้'),
          );

          return of(undefined);
        }),
      );
  }

  private loadDashboard(): Observable<void> {
    this.store.setLoadingDashboard(true);
    this.store.setError(null);

    const today = this.store.endDate();
    const startDate = this.store.startDate();

    if (!today || !startDate) {
      this.store.setError('ไม่พบวันที่สำหรับโหลดข้อมูล');
      this.store.setLoadingDashboard(false);
      return of(undefined);
    }

    // Today
    const todayDate = this.toApiDate(today);

    // Yesterday
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayDate = this.toApiDate(yesterday);

    // This Month
    const startDateApi = this.toApiDate(startDate);
    const endDateApi = this.toApiDate(today);

    // Last Month
    const lastMonthStart = new Date(
      today.getFullYear(),
      today.getMonth() - 1,
      1,
    );

    const lastMonthEnd = new Date(today.getFullYear(), today.getMonth(), 0);

    const lastMonthStartDate = this.toApiDate(lastMonthStart);
    const lastMonthEndDate = this.toApiDate(lastMonthEnd);

    return forkJoin({
      pieChartDaily: this.groupService
        .getEnergySummary({
          start_date: todayDate,
          end_date: todayDate,
        })
        .pipe(
          catchError(() =>
            of({
              meta: {
                response_data: [],
              },
            }),
          ),
        ),

      pieChartYesterday: this.groupService
        .getEnergySummary({
          start_date: yesterdayDate,
          end_date: yesterdayDate,
        })
        .pipe(
          catchError(() =>
            of({
              meta: {
                response_data: [],
              },
            }),
          ),
        ),

      pieChartMonthly: this.groupService
        .getEnergySummary({
          start_date: startDateApi,
          end_date: endDateApi,
        })
        .pipe(
          catchError(() =>
            of({
              meta: {
                response_data: [],
              },
            }),
          ),
        ),

      pieChartLastMonthly: this.groupService
        .getEnergySummary({
          start_date: lastMonthStartDate,
          end_date: lastMonthEndDate,
        })
        .pipe(
          catchError(() =>
            of({
              meta: {
                response_data: [],
              },
            }),
          ),
        ),

      settings: this.settingService.getElectricityBilling(),

      customberInfo: this.settingService.getResidenceProfile(),
    }).pipe(
      tap(
        ({
          pieChartDaily,
          pieChartYesterday,
          pieChartMonthly,
          pieChartLastMonthly,
          settings,
          customberInfo,
        }) => {
          this.store.setEnergyItem(
            mapEnergySummary(pieChartDaily.meta.response_data ?? []),
            mapEnergySummary(pieChartMonthly.meta.response_data ?? []),
          );

          this.store.setComparisonEnergyItem(
            mapEnergySummary(pieChartYesterday.meta.response_data ?? []),
            mapEnergySummary(pieChartLastMonthly.meta.response_data ?? []),
          );

          this.store.setUnit(settings.electricityRate);
          if (customberInfo) {
            this.store.setCustomerSite(mapCustomerSite(customberInfo));
          }
        },
      ),
      map(() => undefined),
      catchError((error) => {
        this.store.setError(
          this.getErrorMsg(error, 'ไม่สามารถโหลดข้อมูล Dashboard ได้'),
        );

        return of(undefined);
      }),
      finalize(() => {
        this.store.setLoadingDashboard(false);
      }),
    );
  }
  private getErrorMsg(err: unknown, fallback: string): string {
    if (
      typeof err === 'object' &&
      err !== null &&
      'message' in err &&
      typeof err.message === 'string'
    )
      return err.message;
    return fallback;
  }

  private toApiDate(date: Date | null): string | undefined {
    if (!date) return undefined;

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');

    const day = String(date.getDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

  // exportCsv(): void {
  //   const items = this.store.energyItems();

  //   if (!items.length) {
  //     return;
  //   }

  //   const headers = ['No', 'Group', 'Energy (kWh)', 'Proportion (%)'];

  //   const rows = items.map((item, index) => [
  //     index + 1,
  //     item.name,
  //     item.energy,
  //     item.proportion,
  //   ]);

  //   const csv = [headers, ...rows].map((row) => row.join(',')).join('\n');

  //   const blob = new Blob([csv], {
  //     type: 'text/csv;charset=utf-8;',
  //   });

  //   const url = URL.createObjectURL(blob);

  //   const link = document.createElement('a');
  //   link.href = url;
  //   link.download = 'energy-summary.csv';
  //   link.click();

  //   URL.revokeObjectURL(url);
  // }

  // createTemplate(template: AnalysisTemplate): Observable<void> {
  //   this.store.setLoadingTemplate(true);
  //   this.store.setError(null);

  //   return this.groupService
  //     .createTemplate(mapCreateTemplatePayload(template))
  //     .pipe(
  //       switchMap((res) => this.loadTemplates(res.meta.response_data.id)),
  //       catchError((error) => {
  //         this.store.setError(
  //           this.getErrorMsg(error, 'ไม่สามารถสร้าง Template ได้'),
  //         );

  //        return of(undefined);
  //       }),
  //       finalize(() => {
  //         this.store.setLoadingTemplate(false);
  //       }),
  //     );
  // }

  // updateTemplate(template: AnalysisTemplate): Observable<void> {
  //   this.store.setLoadingTemplate(true);
  //   this.store.setError(null);

  //   return this.groupService
  //     .updateTemplate(template.id, mapUpdateTemplatePayload(template))
  //     .pipe(
  //       switchMap(() => this.loadTemplates()),
  //       catchError((error) => {
  //         this.store.setError(
  //           this.getErrorMsg(error, 'ไม่สามารถแก้ไข Template ได้'),
  //         );

  //        return of(undefined);
  //       }),
  //       finalize(() => {
  //         this.store.setLoadingTemplate(false);
  //       }),
  //     );
  // }

  // deleteTemplate(template: AnalysisTemplate): Observable<void> {
  //   this.store.setLoadingTemplate(true);
  //   this.store.setError(null);

  //   return this.groupService.deleteTemplate(template.id).pipe(
  //     switchMap(() => this.loadTemplates()),
  //     map(() => void 0),
  //     catchError((error) => {
  //       this.store.setError(
  //         this.getErrorMsg(error, 'ไม่สามารถลบ Template ได้'),
  //       );

  //       return throwError(() => error);
  //     }),
  //     finalize(() => {
  //       this.store.setLoadingTemplate(false);
  //     }),
  //   );
  // }

  // reorderTemplates(templates: AnalysisTemplate[]): Observable<void> {
  //   this.store.setLoadingTemplate(true);
  //   this.store.setError(null);

  //   const payload = {
  //     orders: templates.map((template, index) => ({
  //       id: template.id,
  //       order: templates.length - index,
  //     })),
  //   };

  //   return this.groupService.reorderTemplates(payload).pipe(
  //     switchMap(() => {
  //       const selectedId = this.store.selectedTemplate()?.id;

  //       return this.loadTemplates(selectedId);
  //     }),
  //     map(() => undefined),
  //     catchError((error) => {
  //       this.store.setError(
  //         this.getErrorMsg(error, 'ไม่สามารถเปลี่ยนลำดับ Template ได้'),
  //       );

  //       return throwError(() => error);
  //     }),
  //     finalize(() => {
  //       this.store.setLoadingTemplate(false);
  //     }),
  //   );
  // }
}
